import { Badge } from "@/components/ui/badge";
import type { ExternalAccount } from "@/features/external-accounts/types";
import { IntegrationLogo } from "@/components/molecules/integration-logo";
import { useIntegrationName } from "@/features/integrations/hooks/use-integration-name";
import { useBookingAccountUsage } from "@/features/integrations/hooks/use-booking-accounts";
import { isBookingProvider } from "@/features/external-accounts/types";
import {
  AddressBook,
  CalendarBlank,
  CalendarCheck,
  VideoCamera,
  Envelope,
  Note,
} from "@phosphor-icons/react";
import type { ColumnDef } from "@tanstack/react-table";
import { Action } from "./columns-action";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";

const CapabilityIcon = ({ capability }: { capability: string }) => {
  switch (capability) {
    case "calendar":
      return <CalendarBlank className="h-3 w-3" />;
    case "email":
      return <Envelope className="h-3 w-3" />;
    case "contacts":
      return <AddressBook className="h-3 w-3" />;
    case "notes":
      return <Note className="h-3 w-3" />;
    case "booking":
      return <CalendarCheck className="h-3 w-3" />;
    case "video":
      return <VideoCamera className="h-3 w-3" />;
    default:
      return null;
  }
};

export function useExternalAccountsColumns() {
  const { t } = useTranslation("externalAccounts");
  const { format } = useDateFormat();
  const integrationName = useIntegrationName();
  // A booking account does nothing until a module picks it: say who does.
  const { data: usage = {} } = useBookingAccountUsage();
  const usedBy = (accountId: string) =>
    (usage[accountId] || [])
      .map((user) => user.name || t("table.company"))
      .join(", ");

  const columns: ColumnDef<ExternalAccount>[] = [
    {
      accessorKey: "name",
      header: t("table.account"),
      cell: ({ row }) => {
        const account = row.original;
        return (
          <div className="flex items-center gap-3">
            <IntegrationLogo id={account.provider} size="sm" />
            <div>
              <div className="font-medium">{account.name}</div>
              <div className="text-xs text-muted-foreground truncate max-w-sm">
                {account.provider_identifier}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "provider",
      header: t("table.provider"),
      cell: ({ row }) => {
        const provider = row.original.provider;
        // A Google account connected for its meetings only is, to the user,
        // Google Meet.
        const meetOnly =
          provider === "google" &&
          (row.original.capabilities?.capabilities ?? []).join() === "video";
        return (
          <Badge variant="secondary">
            {integrationName(meetOnly ? "google_meet" : provider)}
          </Badge>
        );
      },
    },
    {
      accessorKey: "capabilities",
      header: t("table.capabilities"),
      cell: ({ row }) => {
        const capabilities = row.original.capabilities?.capabilities || [];
        const booking = isBookingProvider(row.original.provider);
        const users = booking ? usedBy(row.original.id) : "";
        return (
          <div className="space-y-1">
            <div className="flex gap-1">
              {capabilities.map((cap) => (
                <Badge key={cap} variant="outline" className="capitalize">
                  <CapabilityIcon capability={cap} />
                  <span className="ml-1">
                    {t(`capabilities.${cap}.name`, cap)}
                  </span>
                </Badge>
              ))}
            </div>
            {booking && (
              <div className="text-xs text-muted-foreground">
                {users ? t("table.usedBy", { users }) : t("table.unused")}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: t("table.status"),
      cell: ({ row }) => {
        const status = row.original.status;
        const hasError = row.original.last_error;

        return (
          <div className="space-y-1">
            <Badge
              variant={
                status === "active"
                  ? "default"
                  : status === "error"
                    ? "destructive"
                    : "secondary"
              }
              className="capitalize"
            >
              {t(`status.${status}`, status)}
            </Badge>
            {hasError && (
              <div className="text-xs text-red-600">
                {row.original.last_error}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "last_sync_at",
      header: t("table.lastSync"),
      cell: ({ row }) => {
        const lastSync = row.original.last_sync_at;
        // Nothing is synced from a booking software: it is read live.
        if (isBookingProvider(row.original.provider))
          return <span className="text-muted-foreground">—</span>;
        if (!lastSync)
          return (
            <span className="text-muted-foreground">{t("table.never")}</span>
          );

        const date = new Date(lastSync);
        return <div className="text-sm">{format(date, "Pp")}</div>;
      },
    },
    {
      id: "actions",
      header: () => <div className="text-right">{t("table.actions")}</div>,
      cell: ({ row }) => <Action account={row.original} />,
    },
  ];

  return columns;
}
